import { useState, useEffect, useCallback } from 'react';
import {
  isPushNotificationSupported,
  getNotificationPermission,
  setupFCM,
  deleteFCMToken,
  removeFCMToken,
} from '@/lib/fcmService';
import { useAuthProfile } from './use-auth-profile';

export interface UsePushNotificationsReturn {
  isSupported: boolean;
  permission: NotificationPermission;
  isEnabled: boolean;
  isLoading: boolean;
  error: string | null;
  requestPermission: () => Promise<void>;
  enableNotifications: () => Promise<void>;
  disableNotifications: () => Promise<void>;
}

/**
 * Hook for managing push notification permissions and FCM tokens
 */
export function usePushNotifications(): UsePushNotificationsReturn {
  const { user } = useAuthProfile();
  const [isSupported] = useState(() => isPushNotificationSupported());
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    getNotificationPermission()
  );
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentToken, setCurrentToken] = useState<string | null>(null);

  // Check permission status on mount and when user changes
  useEffect(() => {
    if (!isSupported || !user) {
      return;
    }

    const checkPermission = () => {
      const perm = getNotificationPermission();
      setPermission(perm);
      setIsEnabled(perm === 'granted');
    };

    checkPermission();

    // Listen for permission changes (some browsers support this)
    const interval = setInterval(checkPermission, 1000);

    return () => clearInterval(interval);
  }, [isSupported, user]);

  /**
   * Request notification permission and setup FCM
   */
  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      setError('Push notifications are not supported in this browser');
      return;
    }

    if (!user) {
      setError('You must be logged in to enable notifications');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = await setupFCM();
      if (token) {
        setCurrentToken(token);
        setPermission('granted');
        setIsEnabled(true);
        console.log('Push notifications enabled successfully');
      } else {
        setError('Failed to get notification token');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to enable notifications';
      setError(errorMessage);
      console.error('Error requesting permission:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, user]);

  /**
   * Enable push notifications (alias for requestPermission)
   */
  const enableNotifications = useCallback(async () => {
    await requestPermission();
  }, [requestPermission]);

  /**
   * Disable push notifications
   */
  const disableNotifications = useCallback(async () => {
    if (!isSupported || !user) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Remove token from Firestore if we have one
      if (currentToken) {
        await removeFCMToken(currentToken);
      }

      // Delete token from FCM
      await deleteFCMToken();

      setCurrentToken(null);
      setIsEnabled(false);
      console.log('Push notifications disabled successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to disable notifications';
      setError(errorMessage);
      console.error('Error disabling notifications:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, user, currentToken]);

  return {
    isSupported,
    permission,
    isEnabled,
    isLoading,
    error,
    requestPermission,
    enableNotifications,
    disableNotifications,
  };
}
